import { FirearmPoolItemComponentBase } from './firearm-pool-item.component.base';
import { Component, ElementRef, Injector, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { Action, CrudEditorParentService, CrudEditorSelfService, CrudLifecycleEvent, onValueChangeEvent, UserStorage } from '@scisys-uim/uim-common';
import { ASSETS_USER_DETAILS_TYPE, BUSINESS_UNIT_TYPE, BusinessUnit, Firearm, FIREARM_TYPE, FirearmPool, Ranger, RANGER_METADATA, RANGER_TYPE } from 'assets-model';
import { AssetsUserDetailsService, FirearmAssignablePersonService, FirearmPoolService, FirearmService, RefCodeStorage } from 'assets-service';
import { Subject, BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { RangerListComponent } from '../../ranger/search/ranger-search.component';


@Component({
  selector: 'app-firearm-pool-item',
  templateUrl: './firearm-pool-item.component.html',
  styleUrls: ['./firearm-pool-item.component.scss'],
  providers: [CrudEditorParentService],
  viewProviders: [CrudEditorSelfService]
})
export class FirearmPoolItemComponent extends FirearmPoolItemComponentBase {

  memberActions: Action[] = [];
  memberToAdd: Ranger;

  @ViewChild(RangerListComponent) memberList: RangerListComponent;


  memberDefaultDebounceMillis: number = 500;
  @ViewChild('member') memberInput: ElementRef;
  private memberSelectionListSubject: Subject<Ranger[]> = new BehaviorSubject([]);
  memberSelectionList: Observable<Ranger[]> = this.memberSelectionListSubject.asObservable();

  constructor(protected firearmPoolService: FirearmPoolService,
    protected firearmService: FirearmService,
    protected managerService: AssetsUserDetailsService,
    protected firearmAssignablePersonService: FirearmAssignablePersonService,
    protected refCodeStorage: RefCodeStorage,
    protected fb: FormBuilder,
    protected elementRef: ElementRef,
    protected router: Router,
    protected route: ActivatedRoute,
    protected userStorage: UserStorage,
    protected dialog: MatDialog,
    protected injector: Injector,
    protected snackbar: MatSnackBar) {
    super(firearmPoolService, firearmService, managerService, refCodeStorage, fb, elementRef, router, route, userStorage, dialog, injector, snackbar);
  }

  protected onInitAsyncComplete() {
    super.onInitAsyncComplete();
    this.statusLoadSelectionList();

    if (this.createMode && this.routeParams.assetId != null) {
      this.firearmService.get(this.routeParams.assetId).subscribe(firearm => {
        if (!this.readOnly) {
          this.item.firearm = firearm;
        }
        this.setDefaultDisabledFields();
        this.managerLoadSelectionList();
        this.firearmLoadSelectionList();
      });
    } else {
      this.managerLoadSelectionList();
    }

    var self = this;
    this.memberActions = [];
    this.memberActions.push(<Action>{
      title: 'Remove',
      color: 'accent2',
      get available(): boolean {
        return !self.readOnly;
      },
      get enabled(): boolean {
        return true;
      },
      doAction(...args) {
        var element: Ranger = args[0];
        self.removeMember(element);
        self.memberList.reload();
      },
    });
  }

  removeMember(element: Ranger) {
    this.item?.members?.splice(this.item?.members?.indexOf(element), 1);
  }

  ngAfterViewInit(): void {
    this.wireMemberAutocomplete();
  }

  itemSet() {
    super.itemSet();
    this.managerLoadSelectionList();
    this.firearmLoadSelectionList();
    this.addAllWarnings();
  }

  get currentAssignmentIsStorage() {
    return this.item?.firearm?.currentAssignmentFirearmAssignmentType == 'STORAGE';
  }
  get currentAssignmentIsIssue() {
    return this.item?.firearm?.currentAssignmentFirearmAssignmentType == 'ISSUE';
  }

  get currentStorageAssignee() {
    if (!this.currentAssignmentIsStorage) {
      return null;
    }
    return this.item?.firearm?.currentAssignmentEmail;
  }

  get currentIssueAssignee() {
    if (!this.currentAssignmentIsIssue) {
      return null;
    }
    return this.item?.firearm?.currentAssignmentEmail;
  }

  addAllWarnings() {
    super.addAllWarnings();
    this.addWarning('The pool must define at least one member', () => !this.poolHasMembers);
    this.addWarningDynamic(() => 'The Pool Manager is different to the Firearm Storage Assignee: ' + this.currentStorageAssignee, () => {
      if (!this.currentAssignmentIsStorage) {
        return false;
      }
      return this.currentStorageAssignee != this.item?.manager?.user;
    });
    this.addWarningDynamic(() => 'The Firearm is currently issued to a non-member: ' + this.currentIssueAssignee, () => {
      if (!this.currentAssignmentIsIssue) {
        return false;
      }
      return this.item?.members?.find(m => m.email == this.currentIssueAssignee) == null;
    });
    this.addWarningDynamic(() => 'The Ranger is already a member: ' + this.memberDisplay(this.memberToAdd), () => {
      return this.memberToAdd instanceof Ranger && this.containsMember(this.memberToAdd);
    });
  }

  get poolHasMembers(): boolean {
    return this.item?.members != null && this.item?.members?.length > 0;
  }

  isPersistenceEnabled(): boolean {
    return super.isPersistenceEnabled() && this.poolHasMembers;
  }

  createNewItem() {
    return new FirearmPool({
      status: 'A'
    });
  }

  createForm(): FormGroup {
    var form = super.createForm();
    form.addControl('member', this.fb.control(null));
    return form;
  }

  setDefaultDisabledFields(): void {
    super.setDefaultDisabledFields();
    if (!this.form) {
      return;
    }
    this.firearmAssetId?.disable();
    if (!(this.createMode && this.routeParams.assetId == null)) {
      this.firearm?.disable();
    }
    this.createdBy?.disable();
    this.createdDate?.disable();
    this.updatedBy?.disable();
    this.updatedDate?.disable();
  }

  firearmLoadSelectionList() {
    var bu = this.item.firearm?.asset?.businessUnit || this.eAssetsUser?.businessUnit;
    if (this.createMode && this.routeParams.assetId != null) {
      this.firearmSelectionList = [this.item.firearm];
    } else {
      this.firearmService.findByFullFilters({
        assetFilter: {
          buId: bu?.buId
        }
      }).subscribe(
        data => {
          this.firearmSelectionList = FIREARM_TYPE.entityDecorator.ensureContained(this.item && this.item.firearm, data.items);
        }
      );
    }
  }

  businessUnitDisplay(item: BusinessUnit) {
    if (item == null) {
      return "";
    }
    return BUSINESS_UNIT_TYPE.description(item);
  }

  managerLoadSelectionList() {
    var bu = this.item.firearm?.asset?.businessUnit || this.eAssetsUser?.businessUnit;
    this.firearmAssignablePersonService.findByFullFilters({
      buId: bu?.buId,
      assignmentType: 'STORAGE',
      assignmentValid: true
    }).pipe(map(data => {
      return data.items?.map(item => item.user);
    })).subscribe(
      items => {
        this.managerSelectionList = ASSETS_USER_DETAILS_TYPE.entityDecorator.ensureContained(this.item && this.item.manager, items);
        if (this.item != null && this.createMode) {
          if (this.managerSelectionList?.length === 1) {
            this.item.manager = this.managerSelectionList[0];
          } else {
            if (this.item.manager == null && this.currentStorageAssignee != null) {
              var found = this.managerSelectionList?.find(u => u.user == this.currentStorageAssignee);
              if (found) {
                this.item.manager = found;
              }
            }
          }
        }
      }
    );
  }

  allowsAddMember() {
    return !this.readOnly;
  }

  containsMember(member: Ranger) {
    if (member instanceof Ranger) {
      return this.item?.members?.find(m => m.rangerId == member.rangerId) != null;
    }
    return false;
  }

  addMemberEnabled() {
    if (this.memberToAdd instanceof Ranger) {
      return !this.containsMember(this.memberToAdd);
    }
    return false;
  }

  addMember() {
    this.item.members = this.item.members || [];
    this.item.members.push(this.memberToAdd);
    this.memberList.reload();
    this.memberToAdd = null;
  }

  addAllBusinessUnitMembers() {
    this.firearmAssignablePersonService.findByFullFilters({
      buId: this.item.firearm?.asset?.businessUnit?.buId,
      assignmentType: 'ISSUE',
      assignmentValid: true,
      volunteerRanger: false,
    }).pipe(map(data => {
      return data.items?.map(item => item.ranger);
    })).subscribe(items => {
      items?.forEach(r => {
        if (!this.containsMember(r)) {
          this.item.members?.push(r);
        }
      });
      this.memberList.reload();
    });
  }

  isClearMembersEnabled() {
    if (this.item?.members == null) {
      return false;
    }

    if (this.item?.members.length == 0) {
      return false;
    }

    return true;
  }

  clearMembers() {
    if (this.item?.members == null) {
      return;
    }
    [...this.item?.members].forEach(m => this.removeMember(m));
    this.memberList.reload();
  }

  lifecycleEventReceived(event: CrudLifecycleEvent) {
    if (event.eventType == 'undo' && event.stage == 'post') {
      this.memberList.items = this.item?.members || [];
      this.memberList.reload();
    }
  }

  wireMemberAutocomplete(): void {
    if (this.member) {
      onValueChangeEvent(this.member, value => {
        if (value == "") {
          value = null;
          if (this.item) {
            this.memberToAdd = null;
          }
        }
        if (value == null || typeof value == 'string') {
          this.memberSelectionListFilter(value);
        }
      }, {
        debounceMillis: this.getMemberLookupDebounceTime()
      });
    }
  }

  memberSelectionListFilterLoad(text: string): Observable<Ranger[]> {
    return this.firearmAssignablePersonService.findByFullFilters({
      buId: this.item.firearm?.asset?.businessUnit?.buId,
      assignmentType: 'ISSUE',
      volunteerRanger: false,
      text: text
    }).pipe(map(data => {
      return data.items?.map(item => item.ranger);
    }));
  }

  getMemberLookupDebounceTime(): number {
    if (this.systemConfig && this.systemConfig.ranger
      && this.systemConfig.ranger.lookup
      && this.systemConfig.ranger.lookup.debounceTime != null) {
      return this.systemConfig.ranger.lookup.debounceTime;
    }
    return this.memberDefaultDebounceMillis;
  }
  getMemberLookupMinCharacters(): number {
    if (this.systemConfig && this.systemConfig.ranger
      && this.systemConfig.ranger.lookup
      && this.systemConfig.ranger.lookup.minCharacters != null) {
      return this.systemConfig.ranger.lookup.minCharacters;
    }
    return 0;
  }

  memberSelectionListFilter(text: string) {
    const minLookupChars = this.getMemberLookupMinCharacters();
    if (minLookupChars > 0) {
      if (text == null || text.length < minLookupChars) {
        this.memberSelectionListSubject.next([]);
        return;
      }
    }
    this.memberSelectionListFilterLoad(text).subscribe(items => {
      this.memberSelectionListSubject.next(items);
    });
  }


  memberDisplay(item: Ranger) {
    if (item == null) {
      return "";
    }
    if (typeof item == 'string') {
      return item;
    }
    return RANGER_TYPE.description(item);
  }

  memberChanging(value: Ranger, unsetDependents: boolean = true) { }

  memberChanged(value: Ranger, unsetDependents: boolean = true) {
  }

  memberCompare(a: any, b: any) {
    return RANGER_METADATA.equals(a, b);
  }

  get member() {
    return this.control('member');
  }
}





